import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Loader, AlertCircle, ArrowLeft, Home, Eye } from 'lucide-react';
import IdentityEditor from '../components/digital-identity/IdentityEditor';
import api from '../services/api';
import { getFirestore, doc, getDoc } from 'firebase/firestore';

const DigitalIdentityEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [identity, setIdentity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchIdentity = async () => {
      try {
        // First try to get from Firebase
        const db = getFirestore();
        const docRef = doc(db, "digitalIdentities", id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          console.log("Identity found in Firebase:", docSnap.data());
          setIdentity(docSnap.data());
          setLoading(false);
          return;
        }
        
        // If not in Firebase, try the API
        try {
          const response = await api.getIdentity(id);
          setIdentity(response.identity);
        } catch (apiError) {
          console.error('API error:', apiError);
          throw apiError; // Re-throw to be caught by the outer catch
        }
      } catch (error) {
        console.error('Error fetching identity:', error);
        setError('Failed to load digital identity. It may have been deleted or does not exist.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchIdentity();
    }
  }, [id]);

  // Navigation functions
  const handleGoBack = () => {
    navigate('/digital-identity');
  };

  const handleViewIdentity = () => {
    if (id) {
      navigate(`/digital-identity/${id}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <Loader className="h-12 w-12 text-blue-600 animate-spin mb-4" />
        <p className="text-gray-600">Loading digital identity...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="bg-red-50 border border-red-200 text-red-600 p-6 rounded-lg max-w-md w-full flex items-start">
          <AlertCircle className="h-6 w-6 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <h2 className="font-bold text-lg mb-2">Error Loading Identity</h2>
            <p>{error}</p>
            <div className="mt-4 flex space-x-4">
              <button 
                onClick={() => navigate('/digital-identity')}
                className="inline-flex items-center text-blue-600 hover:underline"
              >
                <Home className="h-4 w-4 mr-1.5" />
                Digital Identity Hub
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!identity) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-6 rounded-lg max-w-md w-full flex items-start">
          <AlertCircle className="h-6 w-6 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <h2 className="font-bold text-lg mb-2">Identity Not Found</h2>
            <p>The digital identity you're trying to edit could not be found.</p>
            <div className="mt-4 flex space-x-4">
              <button 
                onClick={() => navigate('/digital-identity')}
                className="inline-flex items-center text-blue-600 hover:underline"
              >
                <Home className="h-4 w-4 mr-1.5" />
                Digital Identity Hub
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      {/* Navigation bar at the top */}
      <div className="max-w-4xl mx-auto px-4 mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleGoBack}
            className="inline-flex items-center text-gray-600 hover:text-blue-600 transition-colors px-3 py-1.5 border border-gray-300 rounded-md hover:border-blue-300 hover:bg-blue-50"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Back to Hub
          </button>
          
          <button
            onClick={handleViewIdentity}
            className="inline-flex items-center text-gray-600 hover:text-blue-600 transition-colors px-3 py-1.5 border border-gray-300 rounded-md hover:border-blue-300 hover:bg-blue-50"
          >
            <Eye className="h-4 w-4 mr-1.5" />
            View Identity
          </button>
        </div>
        
        <div>
          <span className="text-gray-500 text-sm">
            Editing: <span className="font-semibold text-gray-700">{identity.name}</span>
          </span>
        </div>
      </div>
      
      <IdentityEditor existingIdentity={identity} />
    </div>
  );
};

export default DigitalIdentityEdit